import { Plane, Info } from 'lucide-react';
import { useGeo } from '../contexts/GeoContext';

/**
 * Shown when the detected IP country is outside the US.
 * Reminds customers that international orders attract additional postage fees
 * calculated at checkout.
 *
 * Variants:
 *   - "banner" — full-width horizontal strip (cart / checkout)
 *   - "inline" — compact card with border (product detail page)
 *   - "tag"    — small pill (below price)
 */
export default function InternationalShippingNotice({ variant = 'banner' }) {
  const { countryCode, country, loaded } = useGeo();
  if (!loaded) return null;
  // US customers don't see the notice (domestic shipping already free over $100)
  if (!countryCode || countryCode === 'US') return null;

  const countryLabel = country || countryCode;

  if (variant === 'tag') {
    return (
      <span
        data-testid="intl-shipping-tag"
        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-[#FFF7D6] text-[#7A5A00] border border-[#E8C86B] px-2 py-0.5"
      >
        <Plane className="w-3 h-3" />
        Intl postage at checkout
      </span>
    );
  }

  if (variant === 'inline') {
    return (
      <div
        data-testid="intl-shipping-inline"
        className="flex items-start gap-2 bg-[#FFF7D6] border-2 border-[#0A0A0A] p-3 text-xs leading-relaxed"
      >
        <Plane className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-bold uppercase tracking-wider text-[11px]">Shipping to {countryLabel}</p>
          <p className="text-[#525252]">International orders attract postage fees — calculated and displayed at checkout before payment.</p>
        </div>
      </div>
    );
  }

  // default: banner
  return (
    <div
      data-testid="intl-shipping-banner"
      className="flex items-center gap-2 bg-[#0A0A0A] text-white text-xs px-4 py-2 border-b-2 border-[#FFC700]"
    >
      <Info className="w-4 h-4 shrink-0" />
      <p>
        <span className="font-bold uppercase tracking-wider">Shipping to {countryLabel}:</span>
        <span className="ml-2">International orders incur postage fees, calculated at checkout.</span>
      </p>
    </div>
  );
}
